import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@crisellevaldez/speculo-ui';
import { notifyCredentialConnected, notifyCredentialDeleted } from '../../api/rollout';
import { connectProductionCrm, activateProductionCrmSync, disconnectProductionCrm,
  getProductionCrmConnectionStatus, isManagedCrmApp } from '../../api/productionCrmConnections';
import { updateOrganization } from '../../api/remiApi';
import {
  getIntegrationHealth,
  IntegrationHealth,
  NON_OWNER_KEY_WARNING,
} from '../../api/crm';
import { Organization } from '../../types/organization.types';

interface UseRolloutCrmConnectionOptions {
  /** The selected organization */
  selectedOrg?: Organization;
  /** Callback when connection/disconnection succeeds */
  onSuccess?: () => void;
  connectionMode?: 'single_org' | 'shared_account';
  /** Captured org/auth context; stale callbacks may not start another effect. */
  isCurrent?: () => boolean;
}

interface CredentialPayload {
  id: string;
  appKey: string;
  subdomain?: string;
}

/**
 * Converts appKey to a normalized key for storage
 * e.g., 'follow-up-boss' -> 'fub', 'sierra-interactive' -> 'sierra'
 */
const normalizeAppKey = (appKey: string): string => {
  const keyMap: Record<string, string> = {
    'follow-up-boss': 'fub',
    'sierra-interactive': 'sierra',
    'cinc': 'cinc',
    'cinc-pro': 'cinc',
    'brivity': 'brivity',
    'kvcore': 'brivity',
    'boldtrail': 'boldtrail',
    'boldtrail-oauth': 'boldtrail',
  };
  return keyMap[appKey] || appKey.replace(/-/g, '_');
};

/**
 * Converts appKey to a human-readable name for toast messages
 */
const getAppDisplayName = (appKey: string): string => {
  const nameMap: Record<string, string> = {
    'follow-up-boss': 'Follow Up Boss',
    'sierra-interactive': 'Sierra Interactive',
    'cinc': 'CINC',
    'cinc-pro': 'CINC',
    'brivity': 'Brivity',
    'kvcore': 'Brivity',
    'boldtrail': 'BoldTrail',
    'boldtrail-oauth': 'BoldTrail',
    'sure-send': 'Sure Send',
    'salesforce': 'Salesforce',
  };
  return nameMap[appKey] || appKey.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Hook for managing CRM connection state via Rollout
 * Handles connecting, disconnecting, and persisting state via Remi API
 * Supports any CRM dynamically based on appKey
 *
 * @example
 * const { handleCredentialAdded, handleCredentialDeleted, isConnecting, isDisconnecting } = useRolloutCrmConnection({
 *   selectedOrg,
 *   onSuccess: () => refetch(),
 * });
 */
export const useRolloutCrmConnection = ({
  selectedOrg,
  onSuccess,
  connectionMode = 'single_org',
  isCurrent,
}: UseRolloutCrmConnectionOptions) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Helper to update organization's CRM connection status via Remi API
  const updateOrgCrmStatus = async (
    orgId: string,
    appKey: string,
    connected: boolean,
    subdomain?: string,
    mode?: 'single_org' | 'shared_account'
  ) => {
    const crmKey = normalizeAppKey(appKey);
    const existingRollout = selectedOrg?.rollout || {};
    const existingCrm =
      ((existingRollout as Record<string, unknown>)[crmKey] as Record<string, unknown>) || {};

    const crmUpdate = connected
      ? {
          ...existingCrm,
          connected: true,
          connectedAt: new Date().toISOString(),
          ...(subdomain ? { subdomain } : {}),
          ...(appKey === 'follow-up-boss' ? { connectionMode: mode ?? 'single_org' } : {}),
        }
      : {
          connected: false,
          disconnectedAt: new Date().toISOString(),
        };

    await updateOrganization(orgId, {
      rolloutConfig: {
        ...existingRollout,
        [crmKey]: crmUpdate,
      },
    });
  };

  // CONNECT mutation
  const connectMutation = useMutation({
    mutationFn: async ({ id, appKey, subdomain }: CredentialPayload) => {
      const orgId = selectedOrg?.id;
      if (!orgId) throw new Error('Organization ID is required');
      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      if (!id) throw new Error('CRM credential ID is required');

      if (import.meta.env.VITE_APP_ENV === 'production' && isManagedCrmApp(appKey)) {
        const result = await connectProductionCrm(orgId, id, appKey);
        if (result.success !== true || result.organization_id !== orgId ||
            result.app !== appKey || result.status !== 'active') {
          throw new Error('CRM connection confirmation unavailable');
        }
        if (isCurrent && !isCurrent()) return { id, appKey, orgId, connection: {status: "active"}, health: undefined, syncUnconfirmed: true };
        const bound = await getProductionCrmConnectionStatus(orgId);
        if (!['connected', 'sync_paused'].includes(bound.status) || bound.app !== appKey ||
            typeof bound.sync_eligible !== 'boolean') {
          throw new Error('CRM connection readback unavailable');
        }
        let syncUnconfirmed = !bound.sync_eligible;
        if (bound.sync_eligible && (!isCurrent || isCurrent())) {
          try {
            const sync = await activateProductionCrmSync(orgId, appKey, 'bidirectional');
            syncUnconfirmed = sync.status !== 'ok';
          } catch {
            // The binding is already committed. A paused or uncertain sync result
            // must never trigger a second SDK Save or a second org-config write.
            syncUnconfirmed = true;
          }
        }
        const current = await getProductionCrmConnectionStatus(orgId);
        if (!['connected', 'sync_paused'].includes(current.status) || current.app !== appKey ||
            typeof current.sync_enabled !== 'boolean') {
          throw new Error('CRM connection readback unavailable');
        }
        return { id, appKey, orgId, connection: {status: "active"}, health: undefined, syncUnconfirmed: syncUnconfirmed ||
          current.status === 'sync_paused' || current.sync_direction !== 'bidirectional' };
      }



      // Notify backend
      const connection = await notifyCredentialConnected({
        credential_id: id,
        app: appKey,
        organization_id: orgId,
        connection_mode: appKey === 'follow-up-boss' ? connectionMode : 'single_org',
        metadata: appKey === 'follow-up-boss' ? { connection_mode: connectionMode } : undefined,
      });

      // Update CRM connection state via Remi API
      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      await updateOrgCrmStatus(orgId, appKey, true, subdomain, connectionMode);

      // For FUB, read the owner-account status so we can warn instead of
      // claiming success when the connected key isn't an owner-level key.
      let health: IntegrationHealth | undefined;
      if (appKey === 'follow-up-boss' && connectionMode === 'single_org') {
        try {
          health = await getIntegrationHealth(orgId);
        } catch {
          // Non-blocking — treat unknown health as a normal success
        }
      }

      return { id, appKey, orgId, health, connection, syncUnconfirmed: false };
    },
    onSuccess: (data) => {
      if (isCurrent && !isCurrent()) return;
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      queryClient.invalidateQueries({ queryKey: ['rollout-token', selectedOrg?.id] });

      if (data.syncUnconfirmed) {
        showToast({message: `${getAppDisplayName(data.appKey)} connected; CRM sync is paused or unconfirmed.`, variant: 'info'});
      } else if (data.connection.status === 'pending_setup') {
        showToast({
          message: 'Follow Up Boss connected. Map Teams to Speculo organizations to activate sync.',
          variant: 'info',
        });
      } else if (data.health?.owner_account_status === 'non_owner_key') {
        // Seed the cache so the banner shows immediately, and warn instead of
        // showing the "connected successfully" toast.
        queryClient.setQueryData(['integrationHealth', data.orgId], data.health);
        showToast({ message: NON_OWNER_KEY_WARNING, variant: 'warning' });
      } else {
        if (data.health) {
          queryClient.setQueryData(['integrationHealth', data.orgId], data.health);
        }
        const displayName = getAppDisplayName(data.appKey);
        showToast({
          message: `${displayName} connected successfully!`,
          variant: 'success',
        });
      }

      onSuccess?.();
    },
    onError: (error) => {
      if (isCurrent && !isCurrent()) return;
      console.error('Error connecting credential:', error);
      showToast({
        message: 'Failed to connect CRM. Please try again.',
        variant: 'error',
      });
    },
  });

  // DISCONNECT mutation
  const disconnectMutation = useMutation({
    mutationFn: async ({ id, appKey }: CredentialPayload) => {
      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      if (!selectedOrg?.id) throw new Error('Organization ID is required');
      if (!id) throw new Error('CRM credential ID is required');
      const orgId = selectedOrg.id;

      if (import.meta.env.VITE_APP_ENV === 'production' && isManagedCrmApp(appKey)) {
        const result = await disconnectProductionCrm(orgId, id);
        if (result.success !== true || result.organization_id !== orgId ||
            result.status !== 'disconnected') {
          throw new Error('CRM disconnection confirmation unavailable');
        }
        let cleanupUnconfirmed = false;
        try {
          await notifyCredentialDeleted({ credential_id: id, organization_id: orgId });
        } catch {
          // The row is already disconnected; retain that truthful state even
          // when webhook cleanup needs a separate retry.
          cleanupUnconfirmed = true;
        }
        const current = await getProductionCrmConnectionStatus(orgId);
        if (current.status !== 'not_connected') {
          throw new Error('CRM disconnection readback unavailable');
        }
        return { id, appKey, cleanupUnconfirmed };
      }


      // Notify backend
      await notifyCredentialDeleted({
        credential_id: id,
        organization_id: selectedOrg.id,
      });

      // Update CRM disconnection state via Remi API
      if (isCurrent && !isCurrent()) throw new Error('CRM selection changed');
      await updateOrgCrmStatus(selectedOrg.id, appKey, false);

      return { id, appKey, cleanupUnconfirmed: false };
    },
    onSuccess: (data) => {
      if (isCurrent && !isCurrent()) return;
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      queryClient.invalidateQueries({ queryKey: ['rollout-token', selectedOrg?.id] });

      const displayName = getAppDisplayName(data.appKey);
      showToast({
        message: data.cleanupUnconfirmed ? `${displayName} disconnected; CRM webhook cleanup is unconfirmed.` : `${displayName} disconnected successfully`,
        variant: 'info',
      });
      onSuccess?.();
    },
    onError: (error) => {
      if (isCurrent && !isCurrent()) return;
      console.error('Error disconnecting credential:', error);
      showToast({
        message: 'Failed to disconnect CRM. Please try again.',
        variant: 'error',
      });
    },
  });

  // Handlers to be passed to CredentialsManager callbacks
  const handleCredentialAdded = async (credential: CredentialPayload) => {
    console.log('Credential added:', credential.id, credential.appKey);
    return connectMutation.mutateAsync(credential);
  };

  const handleCredentialDeleted = async (credential: CredentialPayload) => {
    console.log('Credential deleted:', credential.id, credential.appKey);
    await disconnectMutation.mutateAsync(credential);
  };

  return {
    /** Handler for CredentialsManager onCredentialAdded callback */
    handleCredentialAdded,
    /** Handler for CredentialsManager onCredentialDeleted callback */
    handleCredentialDeleted,
    /** Whether a connection is in progress */
    isConnecting: connectMutation.isPending,
    /** Whether a disconnection is in progress */
    isDisconnecting: disconnectMutation.isPending,
    /** Connection error if any */
    connectError: connectMutation.error,
    /** Disconnection error if any */
    disconnectError: disconnectMutation.error,
  };
};
